import Cropper from "cropperjs"

// In-app 2:1 cropper backed by Cropper.js v1. Off-ratio product images open this
// modal so the creator can crop to an exact 2:1 instead of being rejected.
//
// openImageCropper(file) resolves with a cropped 2:1 File, or null if cancelled.
// Calls are serialized through a single shared modal so a multi-file gallery drop
// crops one image at a time.

const MIN_WIDTH = 1200
const MIN_HEIGHT = 600
const MAX_WIDTH = 4000
const MAX_HEIGHT = 2000
const STANDARD_WIDTHS = [1200, 1600, 2000, 4000]

let cropQueue = Promise.resolve()

export function openImageCropper(file) {
  // Chain onto the queue so only one modal is ever open at a time.
  const result = cropQueue.then(() => runCropper(file))
  // Keep the queue alive even if a crop errors/cancels.
  cropQueue = result.catch(() => {})
  return result
}

function runCropper(file) {
  return new Promise((resolve) => {
    const $modal = $("#js-image-cropper-modal")
    const $image = $modal.find(".js-cropper-image")
    const $confirm = $modal.find(".js-cropper-confirm")
    const $width = $modal.find(".js-cropper-width")
    const $height = $modal.find(".js-cropper-height")
    const $presets = $modal.find(".js-cropper-presets")
    const $tooSmall = $modal.find(".js-cropper-too-small")
    const objectUrl = URL.createObjectURL(file)

    let cropper = null
    let settled = false

    function cleanup() {
      if (cropper) {
        cropper.destroy()
        cropper = null
      }
      URL.revokeObjectURL(objectUrl)
      $image.attr("src", "")
      $confirm.off("click", onConfirm)
      $width.off("change", onWidthChange)
      $presets.empty()
      $modal.off("hidden.bs.modal", onHidden)
    }

    // Largest 2:1 width the source supports (capped at MAX_WIDTH).
    function feasibleMaxWidth() {
      const img = cropper.getImageData()
      return Math.floor(
        Math.min(img.naturalWidth, img.naturalHeight * 2, MAX_WIDTH)
      )
    }

    // Set the crop box to an exact width (height = width/2), centered and clamped.
    function applyCropWidth(rawWidth) {
      if (!cropper) return
      const img = cropper.getImageData()
      let width = Math.round(Number(rawWidth))
      if (!width) return
      width = Math.max(MIN_WIDTH, Math.min(width, feasibleMaxWidth()))
      const height = Math.round(width / 2)
      cropper.setData({
        width: width,
        height: height,
        x: Math.round((img.naturalWidth - width) / 2),
        y: Math.round((img.naturalHeight - height) / 2),
      })
    }

    // One-click preset buttons for the standard sizes the source can produce.
    function buildPresets() {
      const maxW = feasibleMaxWidth()
      $width.attr("max", maxW)
      $presets.empty()
      STANDARD_WIDTHS.filter((w) => w <= maxW).forEach((w) => {
        $("<button>")
          .attr("type", "button")
          .addClass("btn btn-outline-secondary")
          .text(`${w}×${w / 2}`)
          .on("click", () => applyCropWidth(w))
          .appendTo($presets)
      })
    }

    function onWidthChange() {
      applyCropWidth($width.val())
    }

    function finish(value) {
      if (settled) return
      settled = true
      cleanup()
      resolve(value)
    }

    function onConfirm() {
      if ($confirm.prop("disabled") || !cropper) return

      let canvas = cropper.getCroppedCanvas({
        maxWidth: MAX_WIDTH,
        maxHeight: MAX_HEIGHT,
        imageSmoothingQuality: "high",
      })
      if (!canvas) return

      // Normalize to an exact 2:1 so it passes the ratio check on re-add.
      canvas = normalizeToTwoToOne(canvas)

      const type = file.type || "image/jpeg"
      canvas.toBlob((blob) => {
        // Older Safari can fail to encode WEBP from a canvas — fall back to JPEG.
        if (!blob && type === "image/webp") {
          canvas.toBlob(
            (jpegBlob) => finishWithBlob(jpegBlob, "image/jpeg"),
            "image/jpeg"
          )
          return
        }
        finishWithBlob(blob, type)
      }, type)
    }

    function finishWithBlob(blob, type) {
      if (!blob) {
        finish(null)
        return
      }
      const cropped = new File([blob], file.name, {
        type,
        lastModified: Date.now(),
      })
      $modal.modal("hide") // hidden.bs.modal -> onHidden, but we resolve here
      finish(cropped)
    }

    function onHidden() {
      // Triggered by Cancel, the close button, or our own hide() after a crop.
      finish(null)
    }

    $confirm.on("click", onConfirm)
    $width.on("change", onWidthChange)
    $modal.on("hidden.bs.modal", onHidden)

    $image.attr("src", objectUrl)
    $modal.modal("show")

    cropper = new Cropper($image[0], {
      aspectRatio: 2,
      viewMode: 1,
      autoCropArea: 1,
      background: false,
      responsive: true,
      ready() {
        buildPresets()
      },
      crop(event) {
        // Use event.detail so this works even during Cropper's initial `crop`,
        // before the `cropper` variable is assigned.
        const w = Math.round(event.detail.width)
        const h = Math.round(event.detail.height)
        const tooSmall = w < MIN_WIDTH || h < MIN_HEIGHT
        // Keep the numeric input in sync while dragging — but not while the
        // user is typing into it.
        if (!$width.is(":focus")) $width.val(w)
        $height.text(h)
        $tooSmall.toggleClass("d-none", !tooSmall)
        $confirm.prop("disabled", tooSmall)
        $modal
          .find(".js-cropper-image-container")
          .toggleClass("is-too-small", tooSmall)
      },
    })
  })
}

// getCroppedCanvas can be off-by-a-pixel from a perfect 2:1; redraw to exact ratio.
function normalizeToTwoToOne(canvas) {
  const width = canvas.width
  const height = Math.round(width / 2)
  if (height === canvas.height) return canvas

  const fixed = document.createElement("canvas")
  fixed.width = width
  fixed.height = height
  fixed.getContext("2d").drawImage(canvas, 0, 0, width, height)
  return fixed
}

// Tear down a stuck-open modal before Turbolinks caches the page (mirrors version_form.js).
$(document).on("turbolinks:before-cache", function () {
  const $modal = $("#js-image-cropper-modal")
  if ($("body").hasClass("modal-open")) {
    $modal.hide().removeAttr("aria-modal").attr("aria-hidden", "true")
    $(".modal-backdrop").remove()
    $("body").removeClass("modal-open")
  }
})
